import { computed, toValue } from "vue"
import { useQuery } from "@tanstack/vue-query"
import axios from "axios"

const usePricingPlans = (subscription, planPeriod) => {
  const { isFetching, data } = useQuery({
    queryKey: ["fetchAllPricingPlans"],
    async queryFn() {
      const { data } = await axios.get("/public/plans?all_bundles=1")
      return data
    },
    placeholderData: () => ([]),
  })

  const locale = navigator.languages[0] || navigator.language
  const pricingPlans = computed(() => data.value.map(({ currencyCode, price, ...rest }) => ({
    ...rest,
    currencyCode,
    active: toValue(subscription)?.planId === rest.id,
    price: new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
    }).format(price / 100 * (1 - toValue(subscription)?.discountPercentage / 100)),
  })))

  const currentPlan = computed(() =>
    pricingPlans.value.find(plan => plan.id === toValue(subscription).planId) || {})

  const paidPlans = computed(() => pricingPlans
    .value
    .filter(({ periodUnit, currencyCode }) =>
      periodUnit === toValue(planPeriod) && currencyCode === currentPlan.value.currencyCode)
    .filter(({ periodUnit, active }) => (active === false && periodUnit === "month") || periodUnit === "year")
    .filter(({ bundle }) => !bundle.includes("prod-28-08-2017"))
    .filter(({ name }) => /Hosted/.test(name)),
  )

  const groupedPlans = computed(() => {
    if (toValue(planPeriod) === "month") {
      return { mainPlans: paidPlans.value }
    }
    const currentPlanId = currentPlan.value.id || ""

    // Determine current plan type
    const isTrial = toValue(subscription)?.isTrial || false
    const is3YearMYPA = currentPlanId.includes("3_years_paid_annually")
    const is5YearMYPA = currentPlanId.includes("5_years_paid_annually")
    const is3YearRegular = currentPlan.value.period === 3 && !currentPlanId.includes("paid_annually")
    const is5YearRegular = currentPlan.value.period === 5 && !currentPlanId.includes("paid_annually")
    const isMonthly = currentPlan.value.periodUnit === "month"
    const is1Year = currentPlan.value.period === 1 && !currentPlanId.includes("paid_annually")

    let mainPlans = []
    let multiAnnualPlans = {}

    if (isTrial) {
      // Trial users: Only show 3-year MYPA plans
      mainPlans = paidPlans.value.filter(obj =>
        obj.period === 1
        && obj.id
        && obj.id.includes("3_years_paid_annually"),
      )
      // No multiAnnualPlans for trial - they just select from mainPlans
    }
    else if (is3YearMYPA) {
      // 3-year MYPA users: Only show other 3-year MYPA plans
      mainPlans = paidPlans.value.filter(obj =>
        obj.period === 1
        && obj.id
        && obj.id.includes("3_years_paid_annually"),
      )
      // No multiAnnualPlans for MYPA - they just select from mainPlans
    }
    else if (is5YearMYPA) {
      // 5-year MYPA users: Only show other 5-year MYPA plans
      mainPlans = paidPlans.value.filter(obj =>
        obj.period === 1
        && obj.id
        && obj.id.includes("5_years_paid_annually"),
      )
      // No multiAnnualPlans for MYPA - they just select from mainPlans
    }
    else if (is3YearRegular) {
      // 3-year regular users: Show other 3-year + 5-year plans (no MYPA)
      mainPlans = paidPlans.value.filter(obj => obj.period === 3)

      mainPlans.forEach((parent) => {
        const fiveYearPlans = paidPlans.value.filter(child =>
          child.period === 5 && child.sensors === parent.sensors,
        )
        if (fiveYearPlans.length > 0) {
          multiAnnualPlans[parent.sensors] = fiveYearPlans
        }
      })
    }
    else if (is5YearRegular) {
      // 5-year regular users: Only show other 5-year plans (no MYPA)
      mainPlans = paidPlans.value.filter(obj => obj.period === 5)
      // No multiAnnualPlans for 5-year regular - they just select from mainPlans
    }
    else if (isMonthly) {
      // Monthly users can view both monthly and annual tabs
      if (toValue(planPeriod) === "month") {
        // When monthly users are on the "Monthly" tab - show monthly plans
        mainPlans = paidPlans.value.filter(obj => obj.periodUnit === "month")
      }
      else {
        // When monthly users are on the "Annual/Multi-Annual" tab - show 1-year + 3-year MYPA
        mainPlans = paidPlans.value.filter(obj => obj.period === 1 && !obj.id.includes("paid_annually"))

        mainPlans.forEach((parent) => {
          const threeYearMYPA = paidPlans.value.filter(child =>
            child.period === 1
            && child.id
            && child.id.includes("3_years_paid_annually")
            && child.sensors === parent.sensors,
          )
          if (threeYearMYPA.length > 0) {
            multiAnnualPlans[parent.sensors] = threeYearMYPA
          }
        })
      }
    }
    else if (is1Year) {
      // 1-year users: Show 1-year + 3-year MYPA (no monthly access)
      mainPlans = paidPlans.value.filter(obj => obj.period === 1 && !obj.id.includes("paid_annually"))

      mainPlans.forEach((parent) => {
        const threeYearMYPA = paidPlans.value.filter(child =>
          child.period === 1
          && child.id
          && child.id.includes("3_years_paid_annually")
          && child.sensors === parent.sensors,
        )
        if (threeYearMYPA.length > 0) {
          multiAnnualPlans[parent.sensors] = threeYearMYPA
        }
      })
    }

    return { mainPlans, multiAnnualPlans }
  })

  const trialPlan = computed(() =>
    pricingPlans
      .value
      .filter(({ trial, currencyCode }) =>
        (trial === true && currencyCode === "EUR"))[0],
  )
  const findPlanById = ({ planId }) =>
    pricingPlans.value.find(({ id }) => id === planId) || {}

  return {
    isFetching,
    pricingPlans,
    paidPlans,
    groupedPlans,
    currentPlan,
    trialPlan,
    findPlanById,
  }
}

export {
  usePricingPlans,
}
